import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import StrategyReadyPage from "./pages/StrategyReadyPage";
import DailyPlanPage from "./pages/DailyPlanPage";
import ContentStudioPage from "./pages/ContentStudioPage";
import CopyStudioPage from "./pages/CopyStudioPage";
import BrandAnalyzerPage from "./pages/BrandAnalyzerPage";
import SEOPage from "./pages/SEOPage";
import EmailPage from "./pages/EmailPage";
import MetaAdsPage from "./pages/MetaAdsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import BillingPage from "./pages/BillingPage";
import SettingsPage from "./pages/SettingsPage";
import CompetitorSpyPage from "./pages/CompetitorSpyPage";
import MarketingCoachPage from "./pages/MarketingCoachPage";
import ContentCalendarPage from "./pages/ContentCalendarPage";
import AdCreatorPage from "./pages/AdCreatorPage";
import CharacterGeneratorPage from "./pages/CharacterGeneratorPage";
import CarouselPage from "./pages/CarouselPage";
import CreatorInfluencerPage from "./pages/CreatorInfluencerPage";
import AssetsPage from "./pages/AssetsPage";
import BlogPostCreatorPage from "./pages/BlogPostCreatorPage";
import CaseStudyCreatorPage from "./pages/CaseStudyCreatorPage";
import FaqCreatorPage from "./pages/FaqCreatorPage";
import SocialAccountsPage from "./pages/SocialAccountsPage";
import PublishComposerPage from "./pages/PublishComposerPage";
import PostHistoryPage from "./pages/PostHistoryPage";
import DraftsPage from "./pages/DraftsPage";
import LegalPage from "./pages/LegalPage";
import AdminPage from "./pages/AdminPage";
import AdminNotificationsPage from "./pages/AdminNotificationsPage";
import CompetitiveIntelligencePage from "./pages/CompetitiveIntelligencePage";
import LaunchPageGenerator from "./pages/LaunchPageGenerator";
import TemplateStudioPage from "./pages/TemplateStudioPage";
import FlyerCreatorPage from "./pages/FlyerCreatorPage";
import BlogPage from "./pages/BlogPage";
import VoiceoverPage from "./pages/VoiceoverPage";
import PublicLandingPage from "./pages/PublicLandingPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import AnalyticsDashboardPage from "./pages/AnalyticsDashboardPage";
import ABTestingPage from "./pages/ABTestingPage";
import ContentHistoryPage from "./pages/ContentHistoryPage";
import OnboardingPage from "./pages/OnboardingPage";
import CreateHubPage from "./pages/CreateHubPage";
import IntelligenceHubPage from "./pages/IntelligenceHubPage";
import PitchDeckPage from "./pages/PitchDeckPage";
import AutoCampaignPage from "./pages/AutoCampaignPage";
import WeeklyStrategyPage from "./pages/WeeklyStrategyPage";
import CampaignWorkspacePage from "./pages/CampaignWorkspacePage";
import CreativeStudioPage from "./pages/CreativeStudioPage";
import CampaignAnalyticsPage from "./pages/CampaignAnalyticsPage";
import VisualAdsPage from "./pages/VisualAdsPage";
import AnimationSeriesPage from "./pages/AnimationSeriesPage";
import ProductShootPage from "./pages/ProductShootPage";
import SeoPageLayout from "./components/seo/SeoPageLayout";
import ComparisonPageLayout from "./components/seo/ComparisonPageLayout";

import { UpgradeProvider } from "./components/UpgradeSystem";
import { FeedbackWidget } from "./components/FeedbackWidget";
import { useLocation } from "react-router-dom";

// Meta Pixel SPA PageView tracker — fires on every route change
function MetaPixelTracker() {
  const location = useLocation();
  React.useEffect(() => {
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }, [location.pathname]);
  return null;
}

function AppRoutes() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <MetaPixelTracker />
        <UpgradeProvider>
        <ErrorBoundary>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/legal/:section" element={<LegalPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPage mode="article" />} />
          <Route path="/p/:slug" element={<PublicLandingPage />} />
          <Route path="/pitch" element={<PitchDeckPage />} />

          {/* SEO Cornerstone Pages (Sprint A) */}
          <Route path="/marketing-os" element={<SeoPageLayout slug="marketing-os" />} />
          <Route path="/campaign-studio" element={<SeoPageLayout slug="campaign-studio" />} />
          <Route path="/ad-creative-generator" element={<SeoPageLayout slug="ad-creative-generator" />} />
          <Route path="/landing-page-generator" element={<SeoPageLayout slug="landing-page-generator" />} />
          <Route path="/email-campaigns" element={<SeoPageLayout slug="email-campaigns" />} />
          <Route path="/competitor-analysis" element={<SeoPageLayout slug="competitor-analysis" />} />

          {/* SEO Vertical Pages (Sprint B) */}
          <Route path="/marketing-for-founders" element={<SeoPageLayout slug="marketing-for-founders" />} />
          <Route path="/marketing-for-fashion-brands" element={<SeoPageLayout slug="marketing-for-fashion-brands" />} />
          <Route path="/marketing-for-ecommerce" element={<SeoPageLayout slug="marketing-for-ecommerce" />} />
          <Route path="/marketing-for-creators" element={<SeoPageLayout slug="marketing-for-creators" />} />
          <Route path="/marketing-for-saas" element={<SeoPageLayout slug="marketing-for-saas" />} />
          <Route path="/marketing-for-small-businesses" element={<SeoPageLayout slug="marketing-for-small-businesses" />} />

          {/* SEO Feature Pages (Sprint B) */}
          <Route path="/social-media-campaigns" element={<SeoPageLayout slug="social-media-campaigns" />} />
          <Route path="/content-engine" element={<SeoPageLayout slug="content-engine" />} />
          <Route path="/marketing-calendar" element={<SeoPageLayout slug="marketing-calendar" />} />
          <Route path="/marketing-advisor" element={<SeoPageLayout slug="marketing-advisor" />} />
          <Route path="/growth-strategy" element={<SeoPageLayout slug="growth-strategy" />} />
          <Route path="/brand-positioning" element={<SeoPageLayout slug="brand-positioning" />} />

          {/* Comparison Pages (Sprint C) */}
          <Route path="/scalitos-vs-jasper" element={<ComparisonPageLayout slug="scalitos-vs-jasper" />} />
          <Route path="/scalitos-vs-copy-ai" element={<ComparisonPageLayout slug="scalitos-vs-copy-ai" />} />
          <Route path="/scalitos-vs-marketing-agency" element={<ComparisonPageLayout slug="scalitos-vs-marketing-agency" />} />
          
          {/* Protected Routes */}
          <Route
            path="/strategy-ready"
            element={
              <ProtectedRoute>
                <StrategyReadyPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/daily-plan" 
            element={
              <ProtectedRoute>
                <DailyPlanPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/content-studio" 
            element={
              <ProtectedRoute>
                <ContentStudioPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/dashboard/blog-post"
            element={
              <ProtectedRoute>
                <BlogPostCreatorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/blog-post/:postId"
            element={
              <ProtectedRoute>
                <BlogPostCreatorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/case-study"
            element={
              <ProtectedRoute>
                <CaseStudyCreatorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/faq"
            element={
              <ProtectedRoute>
                <FaqCreatorPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/dashboard/copy-studio" 
            element={
              <ProtectedRoute>
                <CopyStudioPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/launch-page" 
            element={
              <ProtectedRoute>
                <LaunchPageGenerator />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/voiceover" 
            element={
              <ProtectedRoute>
                <VoiceoverPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/brand-analyzer" 
            element={
              <ProtectedRoute>
                <BrandAnalyzerPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/seo" 
            element={
              <ProtectedRoute>
                <SEOPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/email" 
            element={
              <ProtectedRoute>
                <EmailPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/meta-ads" 
            element={
              <ProtectedRoute>
                <MetaAdsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/analytics" 
            element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/billing" 
            element={
              <ProtectedRoute>
                <BillingPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/settings" 
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/competitor-spy" 
            element={
              <ProtectedRoute>
                <CompetitiveIntelligencePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/marketing-coach" 
            element={
              <ProtectedRoute>
                <MarketingCoachPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/content-calendar" 
            element={
              <ProtectedRoute>
                <ContentCalendarPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/ad-creator" 
            element={
              <ProtectedRoute>
                <AdCreatorPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/character-creator" 
            element={
              <ProtectedRoute>
                <CharacterGeneratorPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/carousel-creator" 
            element={
              <ProtectedRoute>
                <CarouselPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/creator-influencer" 
            element={
              <ProtectedRoute>
                <CreatorInfluencerPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/assets" 
            element={
              <ProtectedRoute>
                <AssetsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/flyer-creator" 
            element={
              <ProtectedRoute>
                <FlyerCreatorPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/templates" 
            element={
              <ProtectedRoute>
                <FlyerCreatorPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/template-studio" 
            element={
              <ProtectedRoute>
                <TemplateStudioPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/analytics-dashboard" 
            element={
              <ProtectedRoute>
                <AnalyticsDashboardPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/ab-testing" 
            element={
              <ProtectedRoute>
                <ABTestingPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/content-history" 
            element={
              <ProtectedRoute>
                <ContentHistoryPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/social-accounts" 
            element={
              <ProtectedRoute>
                <SocialAccountsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/publish" 
            element={
              <ProtectedRoute>
                <PublishComposerPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/post-history" 
            element={
              <ProtectedRoute>
                <PostHistoryPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/drafts" 
            element={
              <ProtectedRoute>
                <DraftsPage />
              </ProtectedRoute>
            } 
          />

          {/* New hub pages */}
          <Route path="/dashboard/create" element={<ProtectedRoute><CreateHubPage /></ProtectedRoute>} />
          <Route path="/dashboard/intelligence" element={<ProtectedRoute><IntelligenceHubPage /></ProtectedRoute>} />
          <Route path="/dashboard/advisor" element={<ProtectedRoute><MarketingCoachPage /></ProtectedRoute>} />
          <Route path="/dashboard/auto-campaign" element={<ProtectedRoute><AutoCampaignPage /></ProtectedRoute>} />
          <Route path="/dashboard/strategy" element={<ProtectedRoute><WeeklyStrategyPage /></ProtectedRoute>} />
          <Route path="/dashboard/campaign/:campaignId" element={<ProtectedRoute><CampaignWorkspacePage /></ProtectedRoute>} />
          <Route path="/dashboard/creative-studio" element={<ProtectedRoute><CreativeStudioPage /></ProtectedRoute>} />
          <Route path="/dashboard/campaign-analytics" element={<ProtectedRoute><CampaignAnalyticsPage /></ProtectedRoute>} />
          <Route path="/dashboard/visual-ads" element={<ProtectedRoute><VisualAdsPage /></ProtectedRoute>} />
          <Route path="/dashboard/animation-series" element={<ProtectedRoute><AnimationSeriesPage /></ProtectedRoute>} />
          <Route path="/dashboard/product-shoot" element={<ProtectedRoute><ProductShootPage /></ProtectedRoute>} />
          <Route path="/dashboard/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
          
          <Route
            path="/dashboard/admin"
            element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin/notifications"
            element={
              <ProtectedRoute>
                <AdminNotificationsPage />
              </ProtectedRoute>
            }
          />
          
          {/* Fallback - must be last */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ErrorBoundary>
        </UpgradeProvider>
      </BrowserRouter>
      <Toaster />
      <FeedbackWidget />
    </AuthProvider>
  );
}

export default AppRoutes;
